import React, { useEffect, useRef, useState } from 'react';
import CommonButton from './CommonButton';
import { lightTheme } from '../Constants/Colors';
import { body } from '../Constants/Fonts';

// CommonActionMenu — row actions with automatic overflow.
//
// Convention (see the nmt-frontend-conventions skill): a table row's Actions
// cell should never stack/wrap a pile of buttons. Pass the actions here; when
// there are `inlineThreshold` (default 3) OR MORE, they collapse into a compact
// "⋮" More button that opens a popover menu. Fewer than that render inline as
// plain CommonButtons.
//
// Props:
//   actions         [{ label, onClick, danger?, disabled?, title?, variant?, icon? }]
//   inlineThreshold collapse to the ⋮ menu at this count (default 3)
//   minWidth        popover min width in px (default 180)
//   ariaLabel       accessible name for the ⋮ trigger (default "More actions")
//   iconOnly        render each inline action as a compact ICON button (no text
//                   label) instead of a text button — the label/title becomes the
//                   hover tooltip. Each action picks its glyph via `icon` (a key
//                   of ACTION_ICONS: 'edit' | 'delete' | 'preview' | 'download',
//                   or a custom node). Only applies to inline mode; the overflow
//                   ⋮ menu is unaffected. Defaults to false (text buttons).

const Kebab = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
    </svg>
);

// Shared glyph set for `iconOnly` row actions (stroke icons, inherit color via
// currentColor so danger actions tint red). Pass `icon: 'edit'` etc. on an
// action, or a custom node for anything not covered here.
const Svg = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props} />
);
export const ACTION_ICONS = {
    edit: <Svg><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></Svg>,
    delete: <Svg><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></Svg>,
    preview: <Svg><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></Svg>,
    download: <Svg><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></Svg>,
};

// variant for an inline CommonButton: explicit > danger → error > secondary
const inlineVariant = (a) => a.variant || (a.danger ? 'error' : 'secondary');

// Style for an `iconOnly` action button — a 32×32 bordered square that tints to
// the primary (or danger) colour on hover.
const iconBtnStyle = (a, isHover) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, padding: 0, borderRadius: lightTheme.radiusSm,
    background: isHover && !a.disabled ? lightTheme.surfaceHover : lightTheme.surface,
    border: `1px solid ${isHover && !a.disabled ? (a.danger ? lightTheme.danger : lightTheme.primary) : lightTheme.borderStrong}`,
    color: a.danger ? lightTheme.danger : lightTheme.textSecondary,
    cursor: a.disabled ? 'not-allowed' : 'pointer',
    opacity: a.disabled ? 0.5 : 1,
    transition: 'border-color 0.15s ease, background 0.15s ease',
});

export default function CommonActionMenu({ actions = [], inlineThreshold = 3, minWidth = 180, ariaLabel = 'More actions', iconOnly = false }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const items = actions.filter(Boolean);
    if (items.length === 0) return null;

    // ── Inline mode — below the threshold: render on a single row (nowrap; the
    // row's Actions cell never stacks). `iconOnly` → compact icon buttons with a
    // tooltip; otherwise plain text buttons. ──
    if (items.length < inlineThreshold) {
        return (
            <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                {items.map((a, i) => (iconOnly ? (
                    <button
                        key={a.label ?? i}
                        type="button"
                        title={a.title || a.label}
                        aria-label={a.title || a.label}
                        onClick={a.disabled ? undefined : a.onClick}
                        disabled={a.disabled}
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered((v) => (v === i ? null : v))}
                        style={iconBtnStyle(a, hovered === i)}
                    >
                        {typeof a.icon === 'string' ? (ACTION_ICONS[a.icon] || ACTION_ICONS.edit) : (a.icon || ACTION_ICONS.edit)}
                    </button>
                ) : (
                    <CommonButton
                        key={a.label ?? i}
                        variant={inlineVariant(a)}
                        text={a.label}
                        onClick={a.onClick}
                        disable={a.disabled}
                        title={a.title}
                    />
                )))}
            </span>
        );
    }

    // ── Overflow mode — 3+ actions: a single ⋮ button + popover menu. ──
    const trigger = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        padding: 0,
        background: lightTheme.surface,
        border: `1px solid ${open ? lightTheme.primary : lightTheme.borderStrong}`,
        borderRadius: lightTheme.radiusSm,
        color: lightTheme.textSecondary,
        cursor: 'pointer',
        transition: 'border-color 0.15s ease',
    };

    const menu = {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        minWidth,
        background: lightTheme.surface,
        border: `1px solid ${lightTheme.border}`,
        borderRadius: lightTheme.radius,
        boxShadow: lightTheme.shadowLg,
        padding: 4,
        zIndex: 60,
    };

    return (
        <div ref={boxRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                style={trigger}
                aria-label={ariaLabel}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                {Kebab}
            </button>

            {open && (
                <div style={menu} role="menu">
                    {items.map((a, i) => {
                        const isHover = hovered === i && !a.disabled;
                        return (
                            <div
                                key={a.label ?? i}
                                role="menuitem"
                                aria-disabled={a.disabled || undefined}
                                title={a.title}
                                onMouseEnter={() => setHovered(i)}
                                onMouseLeave={() => setHovered((v) => (v === i ? null : v))}
                                onClick={() => {
                                    if (a.disabled) return;
                                    setOpen(false);
                                    a.onClick?.();
                                }}
                                style={{
                                    ...body(),
                                    padding: '8px 10px',
                                    borderRadius: lightTheme.radiusSm,
                                    cursor: a.disabled ? 'not-allowed' : 'pointer',
                                    opacity: a.disabled ? 0.5 : 1,
                                    color: a.danger ? lightTheme.danger : lightTheme.textSecondary,
                                    background: isHover ? lightTheme.surfaceHover : 'transparent',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {a.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
